import * as React from 'react'
import { Card, CardContent, CardHeader, Input, Button, Label } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getHomePublicAccessToken, regenerateHomePublicAccessToken } from '@/modules/navigation/services/homeSettingsService'
import { listActivePlansIncludingInvisible } from '@/modules/plans/services/memberPlanService'
import { getExclusiveAllowedPlanIds, setExclusiveAllowedPlanIds, getExclusivePopularPlanId, setExclusivePopularPlanId } from '@/modules/settings/services/settingsService'
import ExclusiveLinkPlansSelector from './ExclusiveLinkPlansSelector'

export default function ExclusiveHomeLinkSetting() {
  const [loading, setLoading] = React.useState(true)
  const [token, setToken] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const { add: toast } = useToast()
  const [plans, setPlans] = React.useState<any[]>([])
  const [allowed, setAllowed] = React.useState<string[]>([])
  const [popular, setPopular] = React.useState<string | null>(null)

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const [t, list, ids, pop] = await Promise.all([
          getHomePublicAccessToken(),
          listActivePlansIncludingInvisible(),
          getExclusiveAllowedPlanIds(),
          getExclusivePopularPlanId(),
        ])
        if (mounted) { setToken(t); setPlans(list); setAllowed(ids); setPopular(pop) }
      } catch (e:any) {
        toast({ title: 'Link exclusivo', description: e?.message || 'Falha ao carregar', variant: 'error' })
      } finally { if (mounted) setLoading(false) }
    })()
    return () => { mounted = false }
  }, [])

  const origin = React.useMemo(() => {
    try { return window.location.origin } catch { return '' }
  }, [])

  const shareUrl = React.useMemo(() => {
    const base = origin || ''
    const q = token ? `/?access=${encodeURIComponent(token)}` : '/'
    return `${base}${q}`
  }, [origin, token])

  async function copy() {
    if (!token) return
    try { await navigator.clipboard.writeText(shareUrl); toast({ title: 'Copiado', description: 'Link exclusivo copiado', variant: 'success', soft: true }) } catch {}
  }

  async function ensureOrRegen() {
    if (busy) return
    setBusy(true)
    try {
      const t = await regenerateHomePublicAccessToken()
      setToken(t)
      toast({ title: 'Link atualizado', description: 'Novo token gerado', variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Falha', description: e?.message || 'Não foi possível gerar', variant: 'error' })
    } finally { setBusy(false) }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Link exclusivo para Home</CardHeader>
      <CardContent>
        <div className="space-y-2">
          <p className="text-sm text-foreground/80">
            Permite acesso a Home e demais paginas publicas mesmo com "Pagina inicial: Login" ativado. Compartilhe apenas com quem deve ter acesso temporario.
          </p>
          <div className="flex items-center gap-2">
            <Input value={token ? shareUrl : '— sem link gerado —'} readOnly className="flex-1" />
            <Button variant="secondary" disabled={loading || busy} onClick={ensureOrRegen}>{token ? 'Regerar' : 'Gerar link'}</Button>
            <Button disabled={!token || loading} onClick={copy}>Copiar</Button>
          </div>
          <div className="text-xs text-foreground/60">
            Dica: ao abrir o link, a sessao desta aba fica liberada enquanto o navegador permanecer aberto.
          </div>

          <div className="pt-4 space-y-2">
            <div className="text-sm font-medium">Planos visiveis no link exclusivo</div>
            <div className="text-xs text-foreground/70">Selecione os planos que deverao aparecer quando o acesso for via link exclusivo (mesmo que nao estejam visiveis na pagina Planos).</div>
            <ExclusiveLinkPlansSelector plans={plans} selectedIds={allowed} onChange={setAllowed} popularId={popular} onChangePopular={setPopular} />
            <div className="flex justify-end pt-2">
              <Button disabled={loading || busy} onClick={async ()=> {
                try { setBusy(true); const finalAllowed = popular && !allowed.includes(popular) ? [...allowed, popular] : allowed; await setExclusiveAllowedPlanIds(finalAllowed); await setExclusivePopularPlanId(popular||''); setAllowed(finalAllowed); toast({ title:'Salvo', description:'Planos exclusivos atualizados', variant:'success', soft:true }) }
                catch(e:any){ toast({ title:'Falha', description:e?.message||'Nao foi possivel salvar', variant:'error', soft:true }) }
                finally { setBusy(false) }
              }}>Salvar</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
